import cx from 'classnames'
import { observer } from 'mobx-react-lite'

import { ConnectedFeatureTooltip } from '@src/app/components'
import { useAppStore } from '@src/app/context'
import { useCurrentUser } from '@src/app/user'
import { useCallsDialogContext } from '@src/app/voice/context/useCallsDialogContext'
import IconButton from '@src/component/IconButton'
import Timer from '@src/component/timer'
import type { FeaturesPreviewTrigger } from '@src/service/analytics/WorkspaceAnalyticsStore'
import Badge from '@ui/Badge'
import TextWrap from '@ui/TextWrap'
import Typography from '@ui/Typography'
import { PremiumIcon } from '@ui/icons/tint/10'
import { CallAddPeopleIcon } from '@ui/icons/tint/20/general'

import * as styles from './CallHeader.css'
import { useCallContext } from './context/useCallContext'
import type { OngoingCallMode } from './types'

interface CallHeaderProps {
  mode: OngoingCallMode | null
  timerStartTimestamp?: number | null
  addParticipantsCallback(): void
  openUpgradeModal(trigger: FeaturesPreviewTrigger): void
}

const CallHeader = function ({
  mode,
  timerStartTimestamp,
  addParticipantsCallback,
  openUpgradeModal,
}: CallHeaderProps) {
  const user = useCurrentUser()
  const { call } = useCallContext()
  const { blurred } = useCallsDialogContext()
  const { service } = useAppStore()
  const conferenceCallingCapabilityEnabled =
    service.capabilities.features.conferenceCallingEnabled

  const phoneNumberName = call.isDirectCall ? 'Team call' : call.phoneNumber?.name ?? ''

  return (
    <div className={cx(styles.root, { [styles.rootBlurred]: blurred })}>
      <ConnectedFeatureTooltip
        name={'participantHold3'}
        showForUsersCreatedBefore="2023-08-24"
        isDisabled={!user.settings.web.newFeatureTooltips.participantHold2}
        placement="top"
        autoFocus={false}
        shouldCloseOnScroll={false}
        shouldCloseOnInteractOutside={false}
        offset={8}
        openDelay={1000}
        hideArrow={true}
        title="You can also put everyone on hold"
        description='To put everyone outside of your workspace on hold, navigate to the bottom action bar and click on the "Put on hold" icon.'
        actions={[
          {
            type: 'secondary',
            title: 'Learn more',
            onAction: () => {
              window.open(
                'https://support.openphone.co/hc/en-us/articles/17038519904023-How-to-put-a-caller-on-hold',
                '_blank',
              )
            },
          },
          {
            type: 'primary',
            title: 'Got it',
            isOnCloseTriggered: true,
          },
        ]}
      >
        <div className={styles.featureTooltipAnchor} />
      </ConnectedFeatureTooltip>
      <div className={styles.container}>
        <div className={cx(styles.infoWrapper, { [styles.infoWrapperBlurred]: blurred })}>
          {!call.isDirectCall && call.phoneNumber?.symbol && (
            // @ts-expect-error Provide Typography with a component prop
            <Typography variant="emoji" className={styles.symbol}>
              {call.phoneNumber?.symbol}
            </Typography>
          )}
          {(call.isDirectCall || call.phoneNumber?.name) && (
            <TextWrap value={phoneNumberName}>
              {/*
               // @ts-expect-error Provide Typography with a component prop */}
              <Typography
                nowrap
                variant="caption1"
                fontWeight="medium"
                color="textSecondary"
                className={styles.phoneNumber}
              >
                {phoneNumberName}
              </Typography>
            </TextWrap>
          )}
          {timerStartTimestamp && (
            // @ts-expect-error Provide Typography with a component prop
            <Timer
              variant="caption1"
              fontWeight="medium"
              className={styles.timer}
              startTimestamp={timerStartTimestamp}
            />
          )}
        </div>
        {call.isListener || mode === 'add-participant' ? null : (
          // eslint-disable-next-line custom-rules/no-deprecated-buttons, @typescript-eslint/no-deprecated -- FIXME: https://linear.app/openphone/issue/UXP-4347/migrate-deprecated-buttons-to-ds-button
          <IconButton
            icon={<CallAddPeopleIcon />}
            title={
              conferenceCallingCapabilityEnabled
                ? 'Add people'
                : 'Upgrade to unlock conference calling'
            }
            titleIcon={
              conferenceCallingCapabilityEnabled ? null : (
                <Badge
                  color="purple"
                  icon={<PremiumIcon />}
                  label=""
                  className={styles.premiumBadge}
                />
              )
            }
            className={cx(styles.addParticipants, {
              [styles.addParticipantsBlurred]: blurred,
            })}
            hoverColor="white"
            tooltip={{
              themeKey: 'dark',
              colorScheme: 'call',
            }}
            onClick={
              conferenceCallingCapabilityEnabled
                ? addParticipantsCallback
                : () => openUpgradeModal('group calling')
            }
          />
        )}
      </div>
    </div>
  )
}

export default observer(CallHeader)
